import React, { useState, useEffect } from "react";
import { Navigate, useParams, Link } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import AdminShell from "@/components/admin/AdminShell";
import api, { formatApiErrorDetail, BACKEND_URL } from "@/lib/api";
import { Loader2, Download, Image as ImageIcon, Video, Wand2, Mic, ShieldCheck } from "lucide-react";

const STYLES = [
  { id: "raw",         label: "Raw (no style)" },
  { id: "commercial",  label: "Commercial" },
  { id: "music-promo", label: "Music Promo" },
  { id: "product",     label: "Product Ad" },
  { id: "social",      label: "Social Creative" },
];
const ASPECTS = [
  { id: "9:16", label: "9:16 Vertical" },
  { id: "1:1",  label: "1:1 Square" },
  { id: "16:9", label: "16:9 Wide" },
];
const DURATIONS = [
  { id: 4,  label: "4s" },
  { id: 8,  label: "8s" },
  { id: 12, label: "12s" },
];
const MODELS = [
  { id: "sora-2-pro", label: "Sora 2 Pro · Highest" },
  { id: "sora-2",     label: "Sora 2 · Standard" },
];
const TTS_VOICES = [
  { id: "onyx",    label: "Onyx · Deep male" },
  { id: "echo",    label: "Echo · Warm male" },
  { id: "ash",     label: "Ash · Crisp male" },
  { id: "fable",   label: "Fable · British" },
  { id: "alloy",   label: "Alloy · Neutral" },
  { id: "sage",    label: "Sage · Calm" },
  { id: "nova",    label: "Nova · Bright female" },
  { id: "shimmer", label: "Shimmer · Soft female" },
  { id: "coral",   label: "Coral · Conversational" },
];
const TTS_MODELS = [
  { id: "tts-1-hd", label: "HD · Best quality" },
  { id: "tts-1",    label: "Standard · Faster" },
];

const META = {
  video: {
    eyebrow: "Admin Studio · Private",
    heading: "Studio Video Generation",
    subtitle: "Sora 2 / Sora 2 Pro. No quota, no public watermark — outputs get a discreet bottom YBMStudios.com signature. Renders stay in the admin library, separated from customer renders.",
    breadcrumbs: [{ label: "Owner", to: "/owner" }, { label: "Studio" }, { label: "Video" }],
  },
  image: {
    eyebrow: "Admin Studio · Private",
    heading: "Studio Image Generation",
    subtitle: "Gemini Nano Banana, premium commercial output. Admin signature applied at the bottom; admin library only.",
    breadcrumbs: [{ label: "Owner", to: "/owner" }, { label: "Studio" }, { label: "Image" }],
  },
  voiceover: {
    eyebrow: "Admin Studio · Private",
    heading: "Studio Voiceover Generation",
    subtitle: "OpenAI HD TTS, 9 cinematic voices. Admin-only, no quota, separated from customer renders.",
    breadcrumbs: [{ label: "Owner", to: "/owner" }, { label: "Studio" }, { label: "Voice" }],
  },
};

export default function OwnerStudioPage() {
  const { user, loading } = useAuth();
  const { mode = "video" } = useParams();
  const [recent, setRecent] = useState([]);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (user?.role === "owner") {
      api.get("/admin/studio/renders").then(({ data }) => setRecent(data.renders || [])).catch(() => {});
    }
  }, [user, reloadKey]);

  if (loading || user === undefined) return null;
  if (!user) return <Navigate to="/login?next=/owner/studio/video" replace />;
  if (user.role !== "owner") return <Navigate to="/dashboard" replace />;
  if (!["video", "image", "voiceover"].includes(mode)) return <Navigate to="/owner/studio/video" replace />;

  const meta = META[mode];
  const onCreated = () => setReloadKey((k) => k + 1);

  return (
    <AdminShell
      title={{ eyebrow: meta.eyebrow, heading: meta.heading }}
      subtitle={meta.subtitle}
      breadcrumbs={meta.breadcrumbs}
    >
      {mode === "video"     && <StudioVideo onCreated={onCreated} />}
      {mode === "image"     && <StudioImage onCreated={onCreated} />}
      {mode === "voiceover" && <StudioVoiceover onCreated={onCreated} />}

      <div className="mt-16 border-t border-[#D4AF37]/20 pt-10">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-4 h-4 text-[#D4AF37]" />
            <h2 className="font-display text-2xl font-bold">Admin Render History</h2>
          </div>
          <Link
            to="/owner/renders"
            data-testid="studio-view-all-admin-renders"
            className="text-xs text-[#A0A0A0] hover:text-[#D4AF37] uppercase tracking-wider transition-colors"
          >
            View all →
          </Link>
        </div>
        {recent.length === 0 ? (
          <div className="border border-[#2A2A2A] bg-[#0F0F0F] py-12 text-center text-[#A0A0A0] text-sm" data-testid="studio-recent-empty">
            Nothing yet. Generate your first admin render above.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="studio-recent-grid">
            {recent.slice(0, 8).map((r) => <AdminRenderCard key={r.render_id} r={r} />)}
          </div>
        )}
      </div>
    </AdminShell>
  );
}

function AdminRenderCard({ r }) {
  return (
    <div className="border border-[#2A2A2A] bg-[#0F0F0F] hover:border-[#D4AF37]/50 transition-colors">
      <div className="aspect-square bg-black flex items-center justify-center overflow-hidden">
        {r.kind === "image" ? (
          <img src={`${BACKEND_URL}${r.preview_url}`} alt={r.prompt} loading="lazy" className="w-full h-full object-cover" />
        ) : r.kind === "video" ? (
          <video src={`${BACKEND_URL}${r.preview_url}`} controls playsInline className="w-full h-full object-contain" />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center p-3 gap-2">
            <Mic className="w-8 h-8 text-[#D4AF37]" />
            <audio src={`${BACKEND_URL}${r.preview_url}`} controls className="w-full" />
          </div>
        )}
      </div>
      <div className="p-3">
        <div className="flex items-center gap-2 mb-2">
          {r.kind === "video" ? <Video className="w-3.5 h-3.5 text-[#D4AF37]" /> : r.kind === "audio" ? <Mic className="w-3.5 h-3.5 text-[#D4AF37]" /> : <ImageIcon className="w-3.5 h-3.5 text-[#D4AF37]" />}
          <span className="text-[10px] uppercase tracking-wider text-[#D4AF37]">Admin · {r.kind}</span>
        </div>
        <p className="text-xs text-[#F4F4F0] line-clamp-2 mb-3" title={r.prompt}>{r.prompt}</p>
        <a
          href={`${BACKEND_URL}${r.download_url}`}
          data-testid={`studio-download-${r.render_id}`}
          className="block w-full text-center border border-[#2A2A2A] hover:border-[#D4AF37] text-xs py-2 transition-colors"
        >
          <Download className="w-3 h-3 inline mr-1.5" /> Download
        </a>
      </div>
    </div>
  );
}

// ---------- generator components ----------

function StudioImage({ onCreated }) {
  const [prompt, setPrompt] = useState("");
  const [style, setStyle] = useState("raw");
  const [aspect, setAspect] = useState("9:16");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const onGenerate = async () => {
    setError(""); setResult(null);
    if (prompt.trim().length < 4) { setError("Add at least a few words."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/studio/image", { prompt, style, aspect });
      setResult(data);
      onCreated?.();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <PanelLayout
      controls={
        <>
          <Textarea label="Admin Prompt" value={prompt} onChange={setPrompt} testId="studio-image-prompt"
            placeholder="A premium black product packaging on a marble surface, soft cinematic lighting..." />
          <Selector cols={2} label="Style" options={STYLES} value={style} onChange={setStyle} testId="studio-image-style" />
          <Selector cols={3} label="Aspect" options={ASPECTS} value={aspect} onChange={setAspect} testId="studio-image-aspect" />
          {error && <ErrorRow msg={error} testId="studio-image-error" />}
          <GenerateButton busy={busy} onClick={onGenerate} testId="studio-image-generate" label="Run Admin Image" busyLabel="Generating · Admin…" />
          <AdminPrivacyNote />
        </>
      }
      preview={<PreviewArea busy={busy} result={result} kind="image" />}
    />
  );
}

function StudioVideo({ onCreated }) {
  const [prompt, setPrompt] = useState("");
  const [style, setStyle] = useState("raw");
  const [aspect, setAspect] = useState("9:16");
  const [duration, setDuration] = useState(4);
  const [model, setModel] = useState("sora-2-pro");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const onGenerate = async () => {
    setError(""); setResult(null);
    if (prompt.trim().length < 4) { setError("Add at least a few words."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/studio/video", { prompt, style, aspect, duration, model });
      setResult(data);
      onCreated?.();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <PanelLayout
      controls={
        <>
          <Textarea label="Admin Prompt" value={prompt} onChange={setPrompt} testId="studio-video-prompt"
            placeholder="Cinematic 8s closeup of a luxury watch face, slow rotation, dramatic side lighting..." />
          <Selector cols={2} label="Style" options={STYLES} value={style} onChange={setStyle} testId="studio-video-style" />
          <Selector cols={3} label="Aspect" options={ASPECTS} value={aspect} onChange={setAspect} testId="studio-video-aspect" />
          <Selector cols={3} label="Length" options={DURATIONS} value={duration} onChange={setDuration} testId="studio-video-duration" />
          <Selector cols={2} label="Model" options={MODELS} value={model} onChange={setModel} testId="studio-video-model" />
          {error && <ErrorRow msg={error} testId="studio-video-error" />}
          <GenerateButton busy={busy} onClick={onGenerate} testId="studio-video-generate" label="Run Admin Video" busyLabel="Rendering · Admin (2–5 min)" />
          <AdminPrivacyNote />
        </>
      }
      preview={<PreviewArea busy={busy} result={result} kind="video" />}
    />
  );
}

function StudioVoiceover({ onCreated }) {
  const [text, setText] = useState("");
  const [voice, setVoice] = useState("onyx");
  const [model, setModel] = useState("tts-1-hd");
  const [speed, setSpeed] = useState(1.0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const onGenerate = async () => {
    setError(""); setResult(null);
    if (text.trim().length < 1) { setError("Type the script you want voiced."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/studio/voiceover", { text, voice, model, speed });
      setResult(data);
      onCreated?.();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <PanelLayout
      controls={
        <>
          <Textarea label="Admin Script" value={text} onChange={setText} testId="studio-voice-text"
            placeholder="At YBM Studios, every frame is engineered to convert. Premium ads, on demand." />
          <Selector cols={3} label="Voice" options={TTS_VOICES} value={voice} onChange={setVoice} testId="studio-voice-voice" />
          <Selector cols={2} label="Model" options={TTS_MODELS} value={model} onChange={setModel} testId="studio-voice-model" />
          <div className="mb-5">
            <span className="block text-xs font-bold uppercase tracking-[0.18em] text-[#A0A0A0] mb-2">Speed · {speed.toFixed(2)}×</span>
            <input
              type="range" min="0.5" max="2" step="0.05" value={speed}
              onChange={(e) => setSpeed(parseFloat(e.target.value))}
              data-testid="studio-voice-speed"
              className="w-full accent-[#D4AF37]"
            />
          </div>
          {error && <ErrorRow msg={error} testId="studio-voice-error" />}
          <GenerateButton busy={busy} onClick={onGenerate} testId="studio-voice-generate" label="Run Admin Voiceover" busyLabel="Synthesising · Admin…" />
          <AdminPrivacyNote />
        </>
      }
      preview={<PreviewArea busy={busy} result={result} kind="audio" />}
    />
  );
}

// ---------- shared bits ----------

function PanelLayout({ controls, preview }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      <div className="lg:col-span-2 border border-[#D4AF37]/30 bg-[#121212] p-6 md:p-7 relative">
        <span className="absolute -top-2.5 left-5 px-2 bg-[#050505] text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
          Admin Controls
        </span>
        {controls}
      </div>
      <div className="lg:col-span-3 border border-[#D4AF37]/30 bg-[#0A0A0A] min-h-[480px] flex items-center justify-center p-6 relative">
        <span className="absolute -top-2.5 left-5 px-2 bg-[#050505] text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
          Admin Preview
        </span>
        {preview}
      </div>
    </div>
  );
}

function Textarea({ label, value, onChange, placeholder, testId }) {
  return (
    <label className="block mb-5">
      <span className="block text-xs font-bold uppercase tracking-[0.18em] text-[#A0A0A0] mb-2">{label}</span>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={5}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full bg-[#0A0A0A] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-4 py-3 text-[#F4F4F0] placeholder-[#5A5A5A] transition-colors resize-y"
      />
    </label>
  );
}

function Selector({ label, options, value, onChange, cols = 3, testId }) {
  const colsClass = cols === 2 ? "grid-cols-2" : cols === 3 ? "grid-cols-3" : "grid-cols-4";
  return (
    <div className="mb-5">
      <span className="block text-xs font-bold uppercase tracking-[0.18em] text-[#A0A0A0] mb-2">{label}</span>
      <div className={`grid ${colsClass} gap-2`}>
        {options.map((o) => (
          <button
            key={o.id}
            type="button"
            onClick={() => onChange(o.id)}
            data-testid={`${testId}-${o.id}`}
            className={`px-3 py-2 text-xs md:text-sm border transition-colors ${
              value === o.id
                ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5"
                : "border-[#2A2A2A] text-[#A0A0A0] hover:text-[#F4F4F0]"
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function ErrorRow({ msg, testId }) {
  return (
    <div className="mb-4 px-4 py-3 border border-red-500/40 bg-red-500/10 text-red-300 text-sm" data-testid={testId}>
      <span className="text-[10px] uppercase tracking-[0.22em] text-red-400 font-bold mr-2">Admin Error</span>
      {msg}
    </div>
  );
}

function GenerateButton({ busy, onClick, label, busyLabel, testId }) {
  return (
    <button
      onClick={onClick}
      disabled={busy}
      data-testid={testId}
      className="w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#080808] font-bold py-3.5 hover:bg-[#F0C84B] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> {busyLabel}</> : <><Wand2 className="w-4 h-4" /> {label}</>}
    </button>
  );
}

function AdminPrivacyNote() {
  return (
    <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mt-4 text-center">
      ● Private · Admin signature: YBMStudios.com · Not visible on customer site
    </p>
  );
}

function PreviewArea({ busy, result, kind }) {
  if (busy) {
    return (
      <div className="text-center" data-testid={`studio-${kind}-loading`}>
        <Loader2 className="w-10 h-10 text-[#D4AF37] animate-spin mx-auto mb-4" />
        <p className="text-sm text-[#F4F4F0] font-semibold">Admin render in progress…</p>
        <p className="text-xs text-[#A0A0A0] mt-2">
          {kind === "video" ? "Sora 2 typically takes 2–5 minutes. Stay on this page." : kind === "audio" ? "OpenAI TTS usually 5–20s." : "Image gen usually takes 10–30 seconds."}
        </p>
      </div>
    );
  }
  if (!result) {
    return <div className="text-center text-[#5A5A5A] text-sm">Your admin {kind} will appear here.</div>;
  }
  return (
    <div className="w-full" data-testid={`studio-${kind}-result`}>
      {kind === "image" ? (
        <img src={`${BACKEND_URL}${result.preview_url}`} alt="Generated" className="w-full h-auto max-h-[70vh] object-contain bg-black" />
      ) : kind === "video" ? (
        <video src={`${BACKEND_URL}${result.preview_url}`} controls playsInline className="w-full h-auto max-h-[70vh] bg-black" />
      ) : (
        <div className="w-full bg-black p-10 flex flex-col items-center gap-6">
          <div className="w-24 h-24 rounded-full border-4 border-[#D4AF37] flex items-center justify-center">
            <Mic className="w-10 h-10 text-[#D4AF37]" />
          </div>
          <audio src={`${BACKEND_URL}${result.preview_url}`} controls className="w-full max-w-md" />
          <p className="text-xs text-[#A0A0A0] uppercase tracking-[0.22em]">{result.voice} · {result.model}</p>
        </div>
      )}
      <div className="mt-5 flex flex-col sm:flex-row gap-3 justify-between items-start sm:items-center">
        <span className="text-xs text-[#D4AF37] uppercase tracking-wider flex items-center gap-2">
          <ShieldCheck className="w-3 h-3" />
          Admin render · {kind === "audio" ? `${result.voice} / ${result.model}` : (result.model || "stored in Admin Library")}
        </span>
        <a
          href={`${BACKEND_URL}${result.download_url}`}
          data-testid={`studio-${kind}-download`}
          className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#080808] font-bold px-5 py-2.5 text-sm hover:bg-[#F0C84B] transition-colors"
        >
          <Download className="w-4 h-4" /> Download (Admin)
        </a>
      </div>
    </div>
  );
}
